import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { submitRSVP } from '../utils/api';

const RSVP = ({ invitation, hasRSVP, onRSVPSubmit }) => {
  const { guest_name, max_pax, event_type, code } = invitation;

  const [formData, setFormData] = useState({
    code: code,
    name: guest_name,
    guests: 1,
    attending: null,
    event_attending: null,
    message: '',
  });

  const [submitted, setSubmitted] = useState(hasRSVP);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const eventBoth = event_type === 'both';

  // Update submitted state when hasRSVP changes (after page refresh)
  useEffect(() => {
    setSubmitted(hasRSVP);
  }, [hasRSVP]);

  // Prevent navigation while submitting
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isSubmitting) {
        e.preventDefault();
        e.returnValue = 'Your RSVP is still being submitted. Please wait...';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSubmitting]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAttendingChange = (eventChoice) => {
    setFormData(prev => ({
      ...prev,
      event_attending: eventChoice,
      attending: eventChoice !== 'decline'
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsSubmitting(true);
    setError('');

    console.log('📤 Submitting RSVP...', formData);

    try {
      const result = await submitRSVP(formData);

      if (result.success) {
        console.log('✅ RSVP submitted successfully:', result);

        // Mark as submitted IMMEDIATELY
        setSubmitted(true);
        setIsSubmitting(false);

        // Scroll to show success message
        window.scrollTo({
          top: document.getElementById('rsvp-section')?.offsetTop - 100,
          behavior: 'smooth'
        });

        // Trigger parent component refresh after delay
        // This gives time for the database to commit
        setTimeout(() => {
          if (onRSVPSubmit) {
            onRSVPSubmit();
          }
        }, 2000);
      }
    } catch (err) {
      console.error('❌ RSVP submission failed:', err);
      setError(err.message || 'Failed to submit RSVP. Please try again.');
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  // Generate guest count options
  const guestOptions = [];
  for (let i = 1; i <= max_pax; i++) {
    guestOptions.push(i);
  }

  return (
    <section id="rsvp-section" className="py-32 px-8 bg-cream">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-5xl md:text-7xl mb-6">RSVP</h2>
          <p className="text-lg text-sage mt-8">
            We would love to celebrate with you! Please respond by March 28, 2026
          </p>
        </motion.div>

        <motion.div
          className="bg-white rounded-lg shadow-2xl p-6 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {submitted ? (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-20 h-20 bg-sage text-white rounded-full flex items-center justify-center text-5xl mx-auto mb-8 shadow-lg">
                <FaCheck />
              </div>
              <h3 className="text-4xl mb-4">Thank you!</h3>
              <p className="text-lg text-sage-dark mb-4">
                Your RSVP has been received. We can't wait to celebrate with you!
              </p>
              <div className="bg-cream p-4 rounded-lg mt-6">
                <p className="text-sm text-charcoal">
                  Note: One-time RSVP submission only. If you need to make changes, please contact us directly.
                </p>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-2 tracking-wider">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  readOnly
                  className="w-full px-4 py-3 border-2 border-[#E8E4DD] bg-cream rounded focus:outline-none focus:border-sage focus:bg-white focus:ring-4 focus:ring-sage/10 transition-all pointer-events-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-4 tracking-wider">
                  Will you be attending? *
                </label>

                {eventBoth ? (
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => handleAttendingChange("ceremony")}
                      className={`py-3 px-1 md:py-5 md:px-4 border-2 rounded font-medium transition-all flex items-center text-xs md:text-md justify-center gap-2 ${formData.event_attending === 'ceremony'
                        ? 'bg-sage text-white border-sage shadow-lg'
                        : 'bg-cream text-charcoal border-[#E8E4DD] hover:border-sage hover:-translate-y-1 hover:shadow-md'
                        }`}
                    >
                      Holy Matrimony Only
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAttendingChange("reception")}
                      className={`py-3 px-1 md:py-5 md:px-4 border-2 rounded font-medium transition-all flex items-center text-xs md:text-md justify-center gap-2 ${formData.event_attending === 'reception'
                        ? 'bg-sage text-white border-sage shadow-lg'
                        : 'bg-cream text-charcoal border-[#E8E4DD] hover:border-sage hover:-translate-y-1 hover:shadow-md'
                        }`}
                    >
                      Reception Only
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAttendingChange("both")}
                      className={`py-3 px-1 md:py-5 md:px-4 border-2 rounded font-medium transition-all flex items-center text-xs md:text-md justify-center gap-2 ${formData.event_attending === 'both'
                        ? 'bg-sage text-white border-sage shadow-lg'
                        : 'bg-cream text-charcoal border-[#E8E4DD] hover:border-sage hover:-translate-y-1 hover:shadow-md'
                        }`}
                    >
                      Both Events
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAttendingChange("decline")}
                      className={`py-3 px-1 md:py-5 md:px-4 border-2 rounded font-medium transition-all flex items-center text-xs md:text-md justify-center gap-2 ${formData.event_attending === 'decline'
                        ? 'bg-red-500 text-white border-red-500 shadow-lg'
                        : 'bg-cream text-charcoal border-[#E8E4DD] hover:border-red-500 hover:-translate-y-1 hover:shadow-md'
                        }`}
                    >
                      Regretfully Decline
                    </button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => handleAttendingChange(event_type)}
                      className={`py-3 px-2 md:py-5 md:px-4 border-2 rounded font-medium transition-all flex items-center justify-center gap-2 ${formData.attending === true
                        ? 'bg-sage text-white border-sage shadow-lg'
                        : 'bg-cream text-charcoal border-[#E8E4DD] hover:border-sage hover:-translate-y-1 hover:shadow-md'
                        }`}
                    >
                      <FaCheck className="text-lg" />
                      Joyfully Accepts
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAttendingChange('decline')}
                      className={`py-3 px-2 md:py-5 md:px-4 border-2 rounded font-medium transition-all flex items-center justify-center gap-2 ${formData.attending === false
                        ? 'bg-sage text-white border-sage shadow-lg'
                        : 'bg-cream text-charcoal border-[#E8E4DD] hover:border-sage hover:-translate-y-1 hover:shadow-md'
                        }`}
                    >
                      <FaTimes className="text-lg" />
                      Regretfully Declines
                    </button>
                  </div>
                )}
              </div>

              {formData.attending === true && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                  className="space-y-8"
                >
                  <div>
                    <label htmlFor="guests" className="block text-sm font-medium text-charcoal mb-2 tracking-wider">
                      Number of Guests *
                    </label>
                    <select
                      id="guests"
                      name="guests"
                      value={formData.guests}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-[#E8E4DD] bg-cream rounded focus:outline-none focus:border-sage focus:bg-white focus:ring-4 focus:ring-sage/10 transition-all"
                    >
                      {guestOptions.map(num => (
                        <option key={num} value={num}>
                          {num} Guest{num > 1 ? 's' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </motion.div>
              )}

              <div>
                <label htmlFor="message" className="text-sm font-medium text-charcoal mb-2 tracking-wider">
                  Message to the Couple
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Share your well wishes or any special notes..."
                  className="w-full px-4 py-3 border-2 border-[#E8E4DD] bg-cream rounded focus:outline-none focus:border-sage focus:bg-white focus:ring-4 focus:ring-sage/10 transition-all resize-y min-h-[100px]"
                />
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded text-red-600 text-center">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || formData.event_attending === null}
                className="w-full py-2 px-8 bg-sage text-white rounded-full font-medium text-md tracking-wider transition-all hover:bg-white hover:-translate-y-1 hover:shadow-xl hover:text-sage-dark disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {loading ? 'Submitting...' : 'Submit RSVP'}
              </button>

              <p className="text-xs text-center text-sage-dark mt-4">
                * One-time submission only. Please ensure all details are correct before submitting.
              </p>
            </form>
          )}
        </motion.div>
      </div>

      {/* Submitting Overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] backdrop-blur-sm">
          <div className="bg-white p-8 rounded-2xl shadow-2xl text-center max-w-md mx-4">
            <div className="w-16 h-16 border-4 border-sage border-t-transparent rounded-full mx-auto mb-4 animate-spin"></div>
            <h3 className="text-2xl font-bold mb-2 text-charcoal">Saving Your RSVP...</h3>
            <p className="text-sage-dark mb-4">Please wait, don't close this window</p>
            <div className="text-xs text-gray-500">This will only take a moment</div>
          </div>
        </div>
      )}
    </section>
  );
};

export default RSVP;
